/*
 * @Copyright 2023-2025. Institute for Future Intelligence, Inc.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useStore } from './stores/common';
import * as Selector from './stores/selector';
import { runWithUndo, setWithUndo } from './undo/setWithUndo';
import { cloneLabMoleculeLive } from './undo/liveMoleculeClone';
import { pasteMoleculeWithUndo } from './undo/pasteMolecule';
import { cutMoleculeWithUndo } from './undo/cutMolecule';
import { copyPickedParticles, cutPickedParticles, deletePickedParticles } from './undo/particleEdit';
import { setMessage } from './helpers';
import i18n from './i18n/i18n';
import KeyboardEventHandler from 'react-keyboard-event-handler';
import {
  FlightControl,
  isCrystalCoreProject,
  PickMode,
  ProjectType,
  SpaceshipDisplayMode,
  UNDO_SHOW_INFO_DURATION,
} from './constants';
import { usePrimitiveStore } from './stores/commonPrimitive';
import { askToCreateProject, askToOpenProject, saveProject, saveProjectAs } from './components/mainMenu/projectMenu';
import { resetView, zoomView } from './components/mainMenu/viewMenu';
import { startFlying, stopFlying } from './fly.ts';
import { useRefStore } from './stores/commonRef.ts';
import { Molecule } from './models/Molecule.ts';
import { App, message } from 'antd';
import { UndoableDeleteMoleculeInChamber } from './undo/UndoableDelete.ts';
import type { Undoable } from './undo/Undoable';
import {
  blockedWhileSimulating,
  type LabAtomPosition,
  refuseWhileMinimizing,
  writeLabAtomPositions,
} from './view/engineSync';

export interface KeyboardListenerProps {
  setNavigationView: (selected: boolean) => void;
}

const handleKeys = [
  'a',
  'd',
  'w',
  's',
  'q',
  'e',
  'z',
  'x',
  'y',
  'shift+z',
  'shift+x',
  'shift+y',
  'left',
  'up',
  'right',
  'down',
  'shift+left',
  'shift+up',
  'shift+right',
  'shift+down',
  'ctrl+shift+left',
  'ctrl+shift+up',
  'ctrl+shift+right',
  'ctrl+shift+down',
  'meta+shift+left',
  'meta+shift+up',
  'meta+shift+right',
  'meta+shift+down',
  'ctrl+f',
  'meta+f',
  'ctrl+o',
  'meta+o',
  'ctrl+s',
  'meta+s',
  'ctrl+c',
  'meta+c',
  'ctrl+x',
  'meta+x',
  'ctrl+v',
  'meta+v',
  'ctrl+[',
  'meta+[',
  'ctrl+]',
  'meta+]',
  'ctrl+z',
  'meta+z',
  'ctrl+y',
  'meta+y',
  'ctrl+m',
  'meta+m',
  'ctrl+u', // navigation controls
  'meta+u',
  'ctrl+b',
  'meta+b',
  'shift',
  'esc',
  'ctrl+home',
  'ctrl+alt+h',
  'ctrl+shift+s',
  'meta+shift+s',
  'delete',
  'backspace',
  'alt+backspace',
  'f2',
  'f4',
  'ctrl',
];

const KeyboardListener = React.memo(({ setNavigationView }: KeyboardListenerProps) => {
  const setCommonStore = useStore(Selector.set);
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const language = useStore(Selector.language);
  const undoManager = useStore(Selector.undoManager);
  const addUndoable = useStore(Selector.addUndoable);
  const projectType = useStore(Selector.projectType);
  const updateViewer = usePrimitiveStore(Selector.updateViewer);
  const testMolecules = useStore(Selector.testMolecules);
  const pickedMoleculeIndex = usePrimitiveStore(Selector.pickedMoleculeIndex);
  // The molecule Delete / Cut / Copy act on. A click sets both the single pick and
  // the selection set, but some paths set only the set (undo of a delete or cut;
  // the viewer clears the single pick when a rebuild lands) — the molecule then
  // looked selected while these shortcuts silently did nothing. Fall back to a
  // one-molecule selection set.
  const pickedMoleculeIndices = usePrimitiveStore(Selector.pickedMoleculeIndices);
  const singlePick =
    pickedMoleculeIndex !== -1
      ? pickedMoleculeIndex
      : pickedMoleculeIndices.size === 1
        ? (pickedMoleculeIndices.values().next().value as number)
        : -1;
  const pickedAtomIndices = usePrimitiveStore(Selector.pickedAtomIndices);
  const pickMode = usePrimitiveStore(Selector.pickMode);
  const moleculesRef = useRefStore.getState().moleculesRef;
  const generating = usePrimitiveStore(Selector.generating);

  const { modal } = App.useApp();
  const lang = useMemo(() => {
    return { lng: language };
  }, [language]);

  const toggleNavigationView = () => {
    const next = !useStore.getState().projectState.navigationView;
    setWithUndo('Set Navigation View', !next, next, setNavigationView);
    usePrimitiveStore.getState().set((state) => {
      state.autoRotate = false;
    });
  };

  const toggleAutoRotate = () => {
    const next = !usePrimitiveStore.getState().autoRotate;
    setWithUndo('Auto Rotate', !next, next, (v) =>
      usePrimitiveStore.getState().set((state) => {
        state.autoRotate = v;
      }),
    );
  };

  const onDelete = () => {
    if (refuseWhileMinimizing()) return;
    if (!isCrystalCoreProject(projectType) && deletePickedParticles()) return;
    // Bulk-delete path: more than one molecule in the multi-selection.
    // Snapshot every molecule + its original index so undo can restore
    // them in place. Deletion runs in descending-index order so earlier
    // splices don't shift the indices of yet-to-be-removed items.
    if (pickedMoleculeIndices.size > 1) {
      const snapshots = Array.from(pickedMoleculeIndices)
        .sort((a, b) => a - b)
        // Live frame, not the store's saved one — see liveMoleculeClone.ts.
        .map((idx) => ({
          index: idx,
          molecule: cloneLabMoleculeLive(idx) ?? Molecule.clone(testMolecules[idx], true),
        }));
      const doDelete = () => {
        // Re-snapshot on every delete, Redo included — the molecules may have moved
        // since the first delete (Undo, a run, Redo); see cutMolecule.ts.
        for (const snap of snapshots) snap.molecule = cloneLabMoleculeLive(snap.index) ?? snap.molecule;
        setCommonStore((state) => {
          // Descending order: splice from the back so earlier indices stay valid.
          const descending = [...snapshots].sort((a, b) => b.index - a.index);
          for (const { index } of descending) {
            state.projectState.testMolecules.splice(index, 1);
          }
          if (state.loggable) state.logAction('Delete Selected Molecules');
        });
        usePrimitiveStore.getState().set((state) => {
          state.pickedMoleculeIndex = -1;
          state.pickedMoleculeIndices = new Set<number>();
          state.changed = true;
        });
      };
      const doRestore = () => {
        setCommonStore((state) => {
          // Ascending order: insert from the front so each index lands
          // at its original position.
          for (const { index, molecule } of snapshots) {
            state.projectState.testMolecules.splice(index, 0, molecule);
          }
        });
        usePrimitiveStore.getState().set((state) => {
          state.pickedMoleculeIndices = new Set<number>(snapshots.map((s) => s.index));
        });
      };
      runWithUndo('Delete Selected Molecules', doRestore, doDelete);
      return;
    }
    if (singlePick !== -1) {
      const undoable = {
        name: 'Delete Selected Molecule',
        timestamp: Date.now(),
        index: singlePick,
        molecule: cloneLabMoleculeLive(singlePick) ?? Molecule.clone(testMolecules[singlePick], true),
        undo: () => {
          usePrimitiveStore.getState().set((state) => {
            state.pickedMoleculeIndex = undoable.index;
            // Re-inserting the molecule changes the persisted scene again.
            state.changed = true;
          });
          setCommonStore((state) => {
            state.projectState.testMolecules.splice(singlePick, 0, undoable.molecule);
          });
        },
        redo: () => {
          // Re-snapshot: it may have moved since the first delete (Undo, a run,
          // Redo), and the next Undo must bring it back from HERE.
          undoable.molecule = cloneLabMoleculeLive(undoable.index) ?? undoable.molecule;
          deleteSelectedMolecule(true);
        },
      } as UndoableDeleteMoleculeInChamber;
      addUndoable(undoable);
      deleteSelectedMolecule(false);
    }
  };

  const deleteSelectedMolecule = (redoFlag: boolean) => {
    if (singlePick !== -1) {
      usePrimitiveStore.getState().set((state) => {
        state.pickedMoleculeIndex = -1;
        // The selection set holds list positions too: once this molecule is spliced
        // out, the next one slides into its slot and would inherit the highlight and
        // the transform gizmo. Atom indices shift likewise.
        state.pickedMoleculeIndices = new Set<number>();
        state.pickedAtomIndices = new Set<number>();
        state.pickedAtomIndex = -1;
        state.changed = true;
      });
      setCommonStore((state) => {
        state.projectState.testMolecules.splice(singlePick, 1);
        if (!redoFlag && state.loggable) state.logAction('Delete Selected Molecule');
      });
    }
  };

  // The crystal labs' Cut / Copy / Paste act on their own engine and guest
  // overlay (crystalBuilderMenuItems); the keyboard shortcuts used to call only
  // the molecular-lab versions, so Ctrl+X/C/V did nothing there. All three
  // crystal-core types (as the context menu): checking Crystal Builder alone left
  // Semiconductor / Nanomaterial on the molecular path, where Ctrl+V dropped a
  // molecule copied in an earlier project into this one, unseen. Loaded on
  // demand: this listener is part of the eager lab bundle.
  const crystalClipboard = () => import('./components/contextMenu/crystalBuilderMenuItems');

  const onCut = () => {
    if (isCrystalCoreProject(projectType)) {
      crystalClipboard().then((m) => m.cutPickedCrystalMolecule());
      return;
    }
    if (cutPickedParticles()) return;
    // Undoable, like the menu's Cut (shared helper).
    if (singlePick !== -1) cutMoleculeWithUndo(singlePick);
  };

  const onCopy = () => {
    if (isCrystalCoreProject(projectType)) {
      crystalClipboard().then((m) => m.copyPickedCrystalMolecule());
      return;
    }
    if (copyPickedParticles()) return;
    const m = singlePick !== -1 ? testMolecules[singlePick] : undefined;
    if (m) {
      usePrimitiveStore.getState().set((state) => {
        // The molecule as the user SEES it (live shape + velocities, like Cut) — the
        // store copy is the last saved frame. A default (new-molecule) clone.
        state.copiedMolecule = cloneLabMoleculeLive(singlePick, false) ?? Molecule.clone(m);
        state.cutMolecule = null;
      });
      if (loggable) logAction('Copy Selected Molecule');
    }
  };

  const onPaste = () => {
    if (isCrystalCoreProject(projectType)) {
      crystalClipboard().then((m) => m.pasteCopiedCrystalMolecule());
      return;
    }
    // Undoable, like the menu's Paste (shared helper).
    pasteMoleculeWithUndo();
  };

  // A burst of nudges (a held or repeatedly pressed X/Y/Z key) on the same
  // molecules records ONE undo entry: each press extends the open entry while it
  // is still the latest one and the presses keep coming.
  const nudgeBurstRef = useRef<{ undoable: Undoable; key: string; time: number; after: LabAtomPosition[] } | null>(
    null,
  );

  // X/Y/Z (Shift = negative) moves the selected molecules 1 Å along that axis —
  // the keyboard twin of the transform gizmo's arrow drag, committed the same way
  // (saved project, worker, Reset target, restraint anchors, undo). It used to
  // move only the rendered atoms, so Play snapped the molecule back, a run
  // overwrote it, Save and Reset ignored it and Undo skipped it. Paused only, like
  // the gizmo: mid-run the integrator owns the positions.
  const onTranslation = (direction: 'x' | 'y' | 'z', displacement: number) => {
    const primitive = usePrimitiveStore.getState();
    const molecules = moleculesRef?.current;
    if (!molecules || blockedWhileSimulating()) return; // running, or a pause still settling
    // Same target rule as the gizmo and the context menu's Translate: the
    // multi-selection if there is one, else the single picked molecule.
    const targets =
      primitive.pickedMoleculeIndices.size > 0
        ? Array.from(primitive.pickedMoleculeIndices)
        : primitive.pickedMoleculeIndex !== -1
          ? [primitive.pickedMoleculeIndex]
          : [];
    const before: LabAtomPosition[] = [];
    const after: LabAtomPosition[] = [];
    for (const molIdx of targets) {
      const m = molecules[molIdx];
      if (!m) continue;
      m.atoms.forEach((at, atomIdx) => {
        const r = at.restraint?.position;
        const anchor = r ? { x: r.x, y: r.y, z: r.z } : undefined;
        const p = { molIdx, atomIdx, x: at.position.x, y: at.position.y, z: at.position.z, anchor };
        before.push(p);
        // A restrained atom's anchor moves with it, or Play pulls it back.
        const shifted = { ...p, [direction]: p[direction] + displacement };
        if (anchor) shifted.anchor = { ...anchor, [direction]: anchor[direction] + displacement };
        after.push(shifted);
      });
    }
    if (after.length === 0) return;
    writeLabAtomPositions(after);
    const key = targets.join(',');
    const now = Date.now();
    const burst = nudgeBurstRef.current;
    if (burst && burst.key === key && now - burst.time < 1000 && undoManager.isLastUndo(burst.undoable)) {
      burst.after = after;
      burst.time = now;
    } else {
      const open = { key, time: now, after } as {
        undoable: Undoable;
        key: string;
        time: number;
        after: LabAtomPosition[];
      };
      open.undoable = {
        name: `Translate Selected Molecule${targets.length > 1 ? 's' : ''}`,
        timestamp: now,
        blocked: blockedWhileSimulating,
        undo: () => writeLabAtomPositions(before),
        redo: () => writeLabAtomPositions(open.after),
      };
      nudgeBurstRef.current = open;
      addUndoable(open.undoable);
    }
    if (loggable) logAction('Move Selected Molecule');
  };

  const showThrustFlame = (show: boolean) => {
    usePrimitiveStore.getState().set((state) => {
      state.showThrustFlame = show;
    });
  };

  // Up / Down arrows move the selected X-Y, Y-Z or X-Z plane. The keydown
  // handler below swallows the keyboard's own auto-repeat (a held key must not
  // re-trigger the flight controls), so a held arrow used to move the plane one
  // 0.1 Å step and stop. Now a press moves one step at once and, held past a
  // short delay, keeps stepping at a steady rate until the key is released —
  // the way a text cursor repeats — so a tap is still exactly one step.
  const PLANE_STEP_ANG = 0.1;
  const PLANE_REPEAT_DELAY_MS = 300;
  const PLANE_REPEAT_INTERVAL_MS = 33;
  const planeMoveRef = useRef<{
    delay: ReturnType<typeof setTimeout> | null;
    timer: ReturnType<typeof setInterval> | null;
  }>({ delay: null, timer: null });
  const stepSelectedPlane = (sign: 1 | -1) => {
    // Read the live state each step: the selection or a visibility toggle can
    // change while the key is down.
    const plane = usePrimitiveStore.getState().selectedPlane;
    setCommonStore((state) => {
      const ps = state.projectState;
      switch (plane) {
        case 0:
          if (ps.xyPlaneVisible) ps.xyPlanePosition += sign * PLANE_STEP_ANG;
          break;
        case 1:
          if (ps.yzPlaneVisible) ps.yzPlanePosition += sign * PLANE_STEP_ANG;
          break;
        case 2:
          if (ps.xzPlaneVisible) ps.xzPlanePosition += sign * PLANE_STEP_ANG;
          break;
      }
    });
  };
  const stopPlaneMove = () => {
    const h = planeMoveRef.current;
    if (h.delay) clearTimeout(h.delay);
    if (h.timer) clearInterval(h.timer);
    h.delay = null;
    h.timer = null;
  };
  const startPlaneMove = (sign: 1 | -1) => {
    stopPlaneMove();
    stepSelectedPlane(sign);
    planeMoveRef.current.delay = setTimeout(() => {
      planeMoveRef.current.delay = null;
      planeMoveRef.current.timer = setInterval(() => stepSelectedPlane(sign), PLANE_REPEAT_INTERVAL_MS);
    }, PLANE_REPEAT_DELAY_MS);
  };

  // The plane the arrows move: the selected one, else the first visible one
  // (which becomes selected so its handles show). A project that opens with a
  // plane already on has nothing selected, and selecting by click needs an
  // unobstructed spot ON the plane — a slab that fills the container leaves
  // none. Flying stays on W/S when a plane is showing.
  const planeForArrows = (): number => {
    const selected = usePrimitiveStore.getState().selectedPlane;
    if (selected >= 0) return selected;
    const ps = useStore.getState().projectState;
    const first = ps.xyPlaneVisible ? 0 : ps.yzPlaneVisible ? 1 : ps.xzPlaneVisible ? 2 : -1;
    if (first >= 0) {
      usePrimitiveStore.getState().set((state) => {
        state.selectedPlane = first;
      });
    }
    return first;
  };

  const handleKeyDown = (key: string) => {
    const ship = useStore.getState().projectState.spaceshipDisplayMode === SpaceshipDisplayMode.OUTSIDE_VIEW;
    const navMode = useStore.getState().projectState.navigationView;
    switch (key) {
      // A plane owns the arrows whatever else is picked (clicking a plane in
      // front of a gold plate also picks the plate via the viewer's screen-space
      // hit test; the old no-picked-molecule gate then flew the camera instead).
      case 'up': {
        if (navMode) {
          break;
        }
        if (planeForArrows() >= 0) {
          startPlaneMove(1);
        } else {
          startFlying(FlightControl.MoveForward);
        }
        break;
      }
      case 'down': {
        if (navMode) {
          break;
        }
        if (planeForArrows() >= 0) {
          startPlaneMove(-1);
        } else {
          startFlying(FlightControl.MoveBackward);
        }
        break;
      }
      case 'q':
        if (navMode) {
          break;
        }
        startFlying(ship ? FlightControl.YawLeft : FlightControl.RotateAroundZClockwise);
        break;
      case 'e':
        if (navMode) {
          break;
        }
        startFlying(ship ? FlightControl.YawRight : FlightControl.RotateAroundZCounterclockwise);
        break;
      case 'a':
        if (navMode) {
          showThrustFlame(true);
          break;
        }
        startFlying(ship ? FlightControl.RollLeft : FlightControl.RotateAroundXCounterclockwise);
        break;
      case 'd':
        if (navMode) {
          showThrustFlame(true);
          break;
        }
        startFlying(ship ? FlightControl.RollRight : FlightControl.RotateAroundXClockwise);
        break;
      case 'w':
        if (navMode) {
          showThrustFlame(true);
          break;
        }
        startFlying(ship ? FlightControl.PitchDown : FlightControl.RotateAroundYClockwise);
        break;
      case 's':
        if (navMode) {
          showThrustFlame(true);
          break;
        }
        startFlying(ship ? FlightControl.PitchUp : FlightControl.RotateAroundYCounterclockwise);
        break;
      case 'x':
        if (navMode) {
          showThrustFlame(true);
          break;
        }
        if (projectType === ProjectType.MOLECULAR_MODELING || projectType === ProjectType.POLYMER_BUILDER) {
          onTranslation('x', 1);
        } else {
          startFlying(FlightControl.TranslateInPositiveX);
        }
        break;
      case 'shift+x':
        if (navMode) {
          showThrustFlame(true);
          break;
        }
        if (projectType === ProjectType.MOLECULAR_MODELING || projectType === ProjectType.POLYMER_BUILDER) {
          onTranslation('x', -1);
        } else {
          startFlying(FlightControl.TranslateInNegativeX);
        }
        break;
      case 'y':
        if (navMode) {
          showThrustFlame(true);
          break;
        }
        if (projectType === ProjectType.MOLECULAR_MODELING || projectType === ProjectType.POLYMER_BUILDER) {
          onTranslation('y', 1);
        } else {
          startFlying(FlightControl.TranslateInPositiveY);
        }
        break;
      case 'shift+y':
        if (navMode) {
          showThrustFlame(true);
          break;
        }
        if (projectType === ProjectType.MOLECULAR_MODELING || projectType === ProjectType.POLYMER_BUILDER) {
          onTranslation('y', -1);
        } else {
          startFlying(FlightControl.TranslateInNegativeY);
        }
        break;
      case 'z':
        if (navMode) {
          showThrustFlame(true);
          break;
        }
        if (projectType === ProjectType.MOLECULAR_MODELING || projectType === ProjectType.POLYMER_BUILDER) {
          onTranslation('z', 1);
        } else {
          startFlying(FlightControl.TranslateInPositiveZ);
        }
        break;
      case 'shift+z':
        if (navMode) {
          showThrustFlame(true);
          break;
        }
        if (projectType === ProjectType.MOLECULAR_MODELING || projectType === ProjectType.POLYMER_BUILDER) {
          onTranslation('z', -1);
        } else {
          startFlying(FlightControl.TranslateInNegativeZ);
        }
        break;
      case 'ctrl+[':
      case 'meta+[': // for Mac
        zoomView(0.9);
        break;
      case 'ctrl+]':
      case 'meta+]': // for Mac
        zoomView(1.1);
        break;
      case 'ctrl+c':
      case 'meta+c': // for Mac
        onCopy();
        break;
      case 'ctrl+x':
      case 'meta+x': // for Mac
        onCut();
        break;
      case 'ctrl+v':
      case 'meta+v': // for Mac
        onPaste();
        break;
      case 'ctrl+alt+h': // for Mac and Chrome OS
      case 'ctrl+home':
        resetView();
        break;
      case 'ctrl+u':
      case 'meta+u':
        toggleNavigationView();
        break;
      case 'f4':
      case 'ctrl+m':
      case 'meta+m':
        toggleAutoRotate();
        break;
      case 'ctrl+f':
      case 'meta+f': {
        // for Mac
        if (generating) {
          setMessage('info', i18n.t('message.CannotCreateNewProjectWhileGeneratingMolecule', lang));
        } else {
          askToCreateProject(modal);
        }
        break;
      }
      case 'ctrl+o':
      case 'meta+o': {
        // for Mac
        if (generating) {
          setMessage('info', i18n.t('message.CannotOpenProjectWhileGeneratingMolecule', lang));
        } else {
          askToOpenProject(modal);
        }
        break;
      }
      case 'ctrl+s':
      case 'meta+s': // for Mac
        saveProject();
        break;
      case 'ctrl+shift+s':
      case 'meta+shift+s': // for Mac
        saveProjectAs();
        break;
      case 'alt+backspace':
      case 'backspace':
      case 'delete': {
        onDelete();
        break;
      }
      case 'ctrl+z':
      case 'meta+z': // for Mac
        if (undoManager.undoBlocked()) {
          message.destroy();
          setMessage('warning', i18n.t('experiment.PauseToUndoMove', lang), UNDO_SHOW_INFO_DURATION);
        } else if (undoManager.hasUndo()) {
          const commandName = undoManager.undo();
          // Undo reverts the scene away from the last-saved state, so the
          // project is unsaved again — mark it dirty (mirrors the Edit menu
          // and the agent undo tool).
          usePrimitiveStore.getState().setChanged(true);
          if (commandName) {
            message.destroy();
            setMessage('info', i18n.t('menu.edit.Undo', lang) + ': ' + commandName, UNDO_SHOW_INFO_DURATION);
          }
          if (loggable) logAction('Undo');
        }
        break;
      case 'ctrl+y':
      case 'meta+y': // for Mac
        if (undoManager.redoBlocked()) {
          message.destroy();
          setMessage('warning', i18n.t('experiment.PauseToUndoMove', lang), UNDO_SHOW_INFO_DURATION);
        } else if (undoManager.hasRedo()) {
          const commandName = undoManager.redo();
          usePrimitiveStore.getState().setChanged(true);
          if (commandName) {
            message.destroy();
            setMessage('info', i18n.t('menu.edit.Redo', lang) + ': ' + commandName, UNDO_SHOW_INFO_DURATION);
          }
          if (loggable) logAction('Redo');
        }
        break;
      case 'esc': {
        // Clear any chamber selection. Useful escape hatch from a
        // multi-atom shift-click selection without having to click an
        // empty area of the chamber.
        usePrimitiveStore.getState().set((state) => {
          if (state.pickedAtomIndices.size > 0) state.pickedAtomIndices = new Set<number>();
          if (state.pickedMoleculeIndices.size > 0) state.pickedMoleculeIndices = new Set<number>();
          state.pickedAtomIndex = -1;
          state.pickedMoleculeIndex = -1;
        });
        break;
      }
    }
  };

  // Ctrl/Cmd-A select-all is wired through a window listener rather than
  // react-keyboard-event-handler. The library's matching can favor the
  // bare 'a' registration (already used for flight nav) over the 'ctrl+a'
  // chord, and the browser's native select-all reaches the document
  // listener before the lib gets a chance to consume it. Direct listener
  // with preventDefault sidesteps both.
  const moleculesRefStable = useRefStore.getState().moleculesRef;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
        // Skip when typing in an input — let the user select text there.
        const t = e.target as HTMLElement | null;
        const tag = t?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;
        e.preventDefault();
        const mode = usePrimitiveStore.getState().pickMode;
        const tms = useStore.getState().projectState.testMolecules;
        if (mode === PickMode.ATOM) {
          // Build a global sequential index list that matches
          // ModelUtil.getAtomByIndex's traversal. Atom.index on our model
          // is per-molecule (set at parse time from each Miew complex),
          // so using it directly would alias indices between molecules.
          const live = moleculesRefStable?.current ?? [];
          const indices: number[] = [];
          let g = 0;
          for (const m of live) for (let _ = 0; _ < m.atoms.length; _++) indices.push(g++);
          if (indices.length === 0) return;
          usePrimitiveStore.getState().set((state) => {
            state.pickedAtomIndices = new Set<number>(indices);
            state.pickedAtomIndex = indices[indices.length - 1];
            state.pickedMoleculeIndex = -1;
            state.pickedMoleculeIndices = new Set<number>();
          });
        } else {
          const indices = tms.map((_, i) => i);
          if (indices.length === 0) return;
          usePrimitiveStore.getState().set((state) => {
            state.pickedMoleculeIndices = new Set<number>(indices);
            state.pickedMoleculeIndex = indices[indices.length - 1];
            state.pickedAtomIndex = -1;
            state.pickedAtomIndices = new Set<number>();
          });
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [moleculesRefStable]);

  const handleKeyUp = (key: string) => {
    switch (key) {
      case 'z':
      case 'y':
      case 'x':
      case 'shift+z':
      case 'shift+y':
      case 'shift+x':
      case 'q':
      case 'e':
      case 'a':
      case 'left':
      case 'd':
      case 'right':
      case 'w':
      case 'down':
      case 's':
      case 'up':
        showThrustFlame(false);
        stopFlying();
        stopPlaneMove();
        break;
    }
  };

  useEffect(
    () => () => {
      keyNameRef.current = null;
      // A plane still stepping when the listener unmounts (project switch,
      // route change) must not keep writing to the store.
      stopPlaneMove();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const keyNameRef = useRef<string | null>(null);

  return (
    <>
      <KeyboardEventHandler
        handleKeys={handleKeys}
        handleEventType={'keydown'}
        onKeyEvent={(key, e) => {
          // Undo / redo in a text field belong to the field's own typing
          // history. Running the app-wide undo there (the usual reflex while
          // typing a number) undid an unrelated scene or gallery action — a
          // deleted crystal card came back and shifted the open Crystal Editor
          // onto its neighbor. (Unlike the clipboard keys below, a page text
          // selection does not opt out: only focus in an editable field.)
          if (key === 'ctrl+z' || key === 'meta+z' || key === 'ctrl+y' || key === 'meta+y') {
            const t = e.target as HTMLElement | null;
            const tag = t?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;
          }
          // Clipboard shortcuts: defer to the browser whenever the user has
          // a text selection or focus is in an editable field. Without this
          // bail-out our preventDefault + molecule-copy handler clobbers
          // every document-level copy (e.g. the reasoning stream window).
          if (
            key === 'ctrl+c' ||
            key === 'meta+c' ||
            key === 'ctrl+x' ||
            key === 'meta+x' ||
            key === 'ctrl+v' ||
            key === 'meta+v'
          ) {
            const t = e.target as HTMLElement | null;
            const tag = t?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;
            const sel = window.getSelection()?.toString();
            if (sel) return;
          }
          e.preventDefault();
          if (keyNameRef.current === key) return;
          keyNameRef.current = key;
          handleKeyDown(key);
        }}
      />
      <KeyboardEventHandler
        handleKeys={handleKeys}
        handleEventType={'keyup'}
        onKeyEvent={(key, e) => {
          e.preventDefault();
          keyNameRef.current = null;
          handleKeyUp(key);
        }}
      />
    </>
  );
});

export default KeyboardListener;
